import React, { useMemo, useState } from 'react'
import { Alert, Space, Spin, Table, Tooltip as AntTooltip } from 'antd'
import { useFetchDistributionBetweenPhasesAnalyticsQuery } from '../../../../../../../store/apis/analytics.api'
import { getColorByIndex } from '../../../../../../../utils/colors.helper'
// Title is not needed in the table design
import Text from 'antd/es/typography/Text'
// PatternIcon used in DetailsTable component
import { buildAssignmentPath } from '../../../../../../../utils/link.helper'
import Phases from '../../../../../../../constants/model-phases'
import { useNavigate } from 'react-router-dom'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import MiniStackedBar from './components/MiniStackedBar'
import SimilarityCell from './components/SimilarityCell'
import DetailsTable from './components/DetailsTable'
import { useThemeColors } from '../../../../../../../hooks/useThemeColors'
import useRubricContext from '../../../../../../../hooks/useRubricContext'

const DistributionBetweenPhasesChart = ({ token, phasesToShow = Object.values(Phases) }) => {
    const navigate = useNavigate()
    const colors = useThemeColors()

    const {
        data: analytics,
        isFetching: isFetchingAnalytics,
        error,
    } = useFetchDistributionBetweenPhasesAnalyticsQuery({ token, phasesToShow })

    const { questions, isFetching: isFetchingQuestions } = useRubricContext()

    const isFetching = isFetchingAnalytics || isFetchingQuestions

    const sortedCategoriesLargeToSmall = useMemo(() => {
        if (!analytics?.questionRubrics?.length) return []

        const categorySubmissionsMap = new Map()

        analytics.questionRubrics.forEach(qr => {
            const phase = qr.phases.find(p => p.name === phasesToShow[0])
            if (phase) {
                phase.categories
                    .filter(cat => cat.id !== null)
                    .forEach(cat => {
                        const prevCount = categorySubmissionsMap.get(cat.id) || 0
                        categorySubmissionsMap.set(cat.id, prevCount + cat.numberOfSubmissions)
                    })
            }
        })

        return Array.from(categorySubmissionsMap.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([catId]) => catId)
    }, [analytics, phasesToShow])

    const allCategories = useMemo(() => {
        const categoriesMap = new Map()
        questions?.forEach(question => {
            question.questionRubrics?.forEach(qr => {
                qr.categories?.forEach(category => {
                    categoriesMap.set(category.id, category)
                })
            })
        })
        return Array.from(categoriesMap.values()).sort((a, b) => {
            const aIndex = sortedCategoriesLargeToSmall.indexOf(a.id)
            const bIndex = sortedCategoriesLargeToSmall.indexOf(b.id)
            if (aIndex === -1) return 1
            if (bIndex === -1) return -1
            return aIndex - bIndex
        })
    }, [questions, sortedCategoriesLargeToSmall])

    const allCategoriesById = useMemo(() => {
        const map = new Map()
        allCategories.forEach(cat => map.set(cat.id, cat))
        return map
    }, [allCategories])

    const rows = useMemo(() => {
        if (!analytics?.questionRubrics || !questions) return []

        return analytics.questionRubrics
            .map(analyticsQr => {
                // Find the enriched question to get names and categories
                const question = questions.find(q => q.id === analyticsQr.questionId)
                if (!question) return null

                const questionRubric = question.questionRubrics?.find(qr => qr.id === analyticsQr.id)
                if (!questionRubric) return null

                const displayName = questionRubric.rubric?.name 
                    ? `${question.name} - ${questionRubric.rubric.name}`
                    : question.name

                const byPhase = {}

                phasesToShow.forEach(phaseName => {
                    const phase = analyticsQr.phases.find(p => p.name === phaseName)
                    if (!phase) return

                    const segments = phase.categories
                        .filter(cat => cat.id !== null)
                        .map(cat => {
                            const categoryDetails = questionRubric.categories?.find(c => c.id === cat.id) || allCategoriesById.get(cat.id)
                            if (!categoryDetails) return null
                            return {
                                categoryId: cat.id,
                                categoryName: categoryDetails.name,
                                color: getColorByIndex(categoryDetails.colorId).value,
                                percent: cat.percentFromReviewedSubmissions,
                                count: cat.numberOfSubmissions,
                            }
                        })
                        .filter(Boolean)

                    byPhase[phaseName] = segments
                })

                // Build details rows (per-category exact percentages across phases)
                const categoriesUsedSet = new Set()
                Object.values(byPhase).forEach(segments => segments.forEach(s => categoriesUsedSet.add(s.categoryId)))
                const categoriesUsed = [...categoriesUsedSet]
                categoriesUsed.sort((a, b) => {
                    const aIdx = sortedCategoriesLargeToSmall.indexOf(a)
                    const bIdx = sortedCategoriesLargeToSmall.indexOf(b)
                    if (aIdx === -1) return 1
                    if (bIdx === -1) return -1
                    return aIdx - bIdx
                })

                const categoryRows = categoriesUsed.map(cid => {
                    const catDetails = allCategoriesById.get(cid)
                    const color = catDetails ? getColorByIndex(catDetails.colorId).value : getColorByIndex(0).value
                    const name = catDetails?.name || `Category ${cid}`

                    const row = {
                        key: `${analyticsQr.id}-${cid}`,
                        categoryId: cid,
                        categoryName: name,
                        color,
                    }

                    phasesToShow.forEach(phaseName => {
                        const seg = (byPhase[phaseName] || []).find(s => s.categoryId === cid)
                        row[phaseName] = seg ? seg.percent : 0
                    })

                    return row
                })

                return {
                    key: analyticsQr.id,
                    questionRubricId: analyticsQr.id,
                    name: displayName,
                    isHighDivergent: analyticsQr.isHighDivergent,
                    similarityScore: analyticsQr.similarityScore,
                    byPhase,
                    details: categoryRows,
                }
            })
            .filter(Boolean)
    }, [analytics, questions, phasesToShow, allCategoriesById, sortedCategoriesLargeToSmall])

    const [expandedRowKeys, setExpandedRowKeys] = useState([])

    if (error) return <Alert message={error} type='error' showIcon />

    const columns = [
        {
            title: 'Rubric',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <Space size={8} style={{ cursor: 'pointer' }} onClick={() => navigate(buildAssignmentPath(token, record.questionRubricId))}>
                    {record.isHighDivergent && (
                        <AntTooltip title='High Divergence Across Phases'>
                            <ExclamationCircleOutlined style={{ color: colors.error }} />
                        </AntTooltip>
                    )}
                    <Text>{text}</Text>
                </Space>
            ),
            width: 280,
            ellipsis: true,
        },
        {
            title: 'Similarity',
            dataIndex: 'similarityScore',
            key: 'similarityScore',
            render: (value, record) => <SimilarityCell value={value} isDivergent={record.isHighDivergent} />,
            width: 140,
            sorter: (a, b) => a.similarityScore - b.similarityScore,
        },
        ...phasesToShow.map(phaseName => ({
            title: phaseName,
            dataIndex: ['byPhase', phaseName],
            key: phaseName,
            render: (segments, record) => (
                <MiniStackedBar
                    segments={segments}
                    onClick={() => navigate(buildAssignmentPath(token, record.questionRubricId))}
                />
            ),
        })),
    ]

    const expandedRowRender = (record) => (
        <DetailsTable record={record} phasesToShow={phasesToShow} />
    )

    return (
        <Spin spinning={isFetching} tip='Loading...'>
            <Table
                columns={columns}
                dataSource={rows}
                pagination={false}
                rowKey='key'
                expandable={{
                    expandedRowRender,
                    expandedRowKeys,
                    onExpand: (expanded, record) => {
                        setExpandedRowKeys(prev => {
                            if (expanded) return [...prev, record.key]
                            return prev.filter(k => k !== record.key)
                        })
                    },
                }}
                onRow={record => ({
                    style: record.isHighDivergent
                        ? { backgroundColor: colors.errorBg } // AntD error light background
                        : {},
                })}
                scroll={{ x: true }}
            />
        </Spin>
    )
}

export default DistributionBetweenPhasesChart
